import { useCallback, useMemo } from 'react'
import { useMediaQuery } from '@mantine/hooks'
import { ClientLogo } from 'common/components'
import { useMessageActions, useSelector } from 'common/hooks'
import { APPLICATION_ID, getApplicationLogoPath } from 'common/utils'
import { isNil } from 'lodash'
import { ROUTES } from 'services'
import { UserMenu } from 'user/components'

import Menu from './Menu'
import MobileMenu from './MobileMenu'

/**
 * HeaderWrapper - komponent głównego headera aplikacji
 *
 * Logika przekazywania propsów do komponentu Menu:
 *
 * Tablet (768-1024px):
 *   - visibleMenu: pierwszy element menu (menu[0])
 *   - hiddenMenu: wszystkie pozostałe elementy (menu[1..n]) - wyświetlane w menu "more"
 *
 * Desktop (>=1024px):
 *   - visibleMenu: wszystkie elementy menu (menu[0..n])
 *   - hiddenMenu: pusty array [] - menu "more" nie jest wyświetlane
 *
 * Mobile (<768px):
 *   - Menu nie jest renderowane (ukryte przez className 'hidden md:flex')
 *   - Zamiast tego wyświetlane jest MobileMenu (hamburger)
 */
export default function HeaderWrapper() {
  const { menu } = useSelector((state) => state.iframe)
  const { sendApprovalBackToHomeFromDetailsMessage } = useMessageActions()

  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1023px)')
  const isDesktop = useMediaQuery('(min-width: 1024px)')

  const applicationId = useMemo(() => localStorage.getItem('application_id'), [])

  const visibleLinkCount = useMemo(() => {
    if (!menu) return null

    if (isTablet) return 1

    if (isDesktop) return menu.length

    return null
  }, [menu, isTablet, isDesktop])

  const handleRedirectToHomePage = useCallback(
    () => sendApprovalBackToHomeFromDetailsMessage({ to: 'HOME' }),
    [sendApprovalBackToHomeFromDetailsMessage],
  )

  return (
    <div className={'flex items-center justify-between h-[60px] min-w-0 gap-4'}>
      <div className={'flex items-center flex-1 min-w-0 gap-4 justify-between'}>
        <div className={'flex items-center flex-shrink-0'}>
          <ClientLogo
            classes={{
              link:
                applicationId === APPLICATION_ID.CTK_LOGISTICS ? 'h-[32px]' : 'h-[24px]',
            }}
            {...getApplicationLogoPath()}
            link={ROUTES.home()}
            onClick={handleRedirectToHomePage}
          />
        </div>
        {menu && (
          <>
            <div className={'hidden md:flex flex-1 min-w-0'}>
              <Menu
                hiddenMenu={menu.slice(
                  isNil(visibleLinkCount) ? menu.length : visibleLinkCount,
                  menu.length,
                )}
                visibleMenu={menu.slice(
                  0,
                  visibleLinkCount === 0 ? 0 : visibleLinkCount || menu.length,
                )}
              />
            </div>
            <MobileMenu />
          </>
        )}
      </div>
      <div className={'flex items-center flex-shrink-0 gap-4'}>
        <UserMenu />
      </div>
    </div>
  )
}
